import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext.tsx';
import DonationModal from './DonationModal';
import '../styles/EventProposalForm.css';

export default function EventActivityForm({ trail, poi, nameRegion, onClose, onSuccess }) {
    const { user, isAuthenticated, loginWithGoogle, logout, loading: authLoading } = useAuth();

    const [formData, setFormData] = useState({
        eventName: '',
        eventDate: '',
        organizerPhone: '',
        maxParticipants: '',
        description: '',
        meetingPoint: '',
        meetingTime: '',
        difficulty: trail?.properties?.difficulty || '',
        notes: '',
        isRecurring: false,
        recurringDays: [],
        recurringEndDate: ''
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const [error, setError] = useState(null);
    const [showDonation, setShowDonation] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    const WEEKDAYS = [
        { id: 1, short: 'Lun', full: 'Lunedì' },
        { id: 2, short: 'Mar', full: 'Martedì' },
        { id: 3, short: 'Mer', full: 'Mercoledì' },
        { id: 4, short: 'Gio', full: 'Giovedì' },
        { id: 5, short: 'Ven', full: 'Venerdì' },
        { id: 6, short: 'Sab', full: 'Sabato' },
        { id: 0, short: 'Dom', full: 'Domenica' }
    ];

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox' && name === 'isRecurring') {
            setFormData(prev => ({
                ...prev,
                isRecurring: checked,
                recurringDays: checked ? prev.recurringDays : [],
                recurringEndDate: checked ? prev.recurringEndDate : ''
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const toggleWeekday = (dayId) => {
        setFormData(prev => {
            const days = prev.recurringDays.includes(dayId)
                ? prev.recurringDays.filter(d => d !== dayId)
                : [...prev.recurringDays, dayId];
            return { ...prev, recurringDays: days };
        });
    };

    const generateRecurringDates = (startDate, endDate, selectedDays) => {
        const dates = [];
        let current = new Date(startDate);
        const end = new Date(endDate);
        while (current <= end) {
            if (selectedDays.includes(current.getDay())) {
                dates.push(new Date(current));
            }
            current.setDate(current.getDate() + 1);
        }
        return dates;
    };

    const handleGoogleLogin = async () => {
        setIsLoggingIn(true);
        setError(null);
        try {
            await loginWithGoogle();
        } catch (err) {
            setError('Login Google fallito. Riprova.');
        } finally {
            setIsLoggingIn(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isAuthenticated || !user) {
            setError('Devi accedere con Google prima di proporre un evento');
            return;
        }

        setIsSubmitting(true);
        setError(null);

        if (formData.isRecurring) {
            if (formData.recurringDays.length === 0) {
                setError('Seleziona almeno un giorno della settimana per la ricorrenza');
                setIsSubmitting(false);
                return;
            }
            if (!formData.recurringEndDate) {
                setError('Specifica una data di fine per l\'evento ricorrente');
                setIsSubmitting(false);
                return;
            }
            if (new Date(formData.recurringEndDate) <= new Date(formData.eventDate)) {
                setError('La data di fine deve essere successiva alla data di inizio');
                setIsSubmitting(false);
                return;
            }
        }

        try {
            const apiUrl = process.env.REACT_APP_API_URL_NODE || 'http://localhost:3002';
            const token = await user.getIdToken();

            let datesToCreate = [formData.eventDate];
            if (formData.isRecurring) {
                datesToCreate = generateRecurringDates(
                    formData.eventDate,
                    formData.recurringEndDate,
                    formData.recurringDays
                ).map(d => d.toISOString().split('T')[0]);
            }

            const baseEventPayload = {
                eventType: 'activity',
                eventName: formData.eventName,
                // Dati organizzatore da Google — backward compat: i vecchi eventi
                // non hanno questi campi e continuano a mostrare organizerName/Email
                organizerName: user.displayName || '',
                organizerEmail: user.email || '',
                organizerPhone: formData.organizerPhone,
                // Campi Firebase per la nuova autenticazione
                proposerUid: user.uid,
                proposerDisplayName: user.displayName || '',
                proposerPhoto: user.photoURL || '',
                maxParticipants: formData.maxParticipants,
                description: formData.description,
                meetingPoint: formData.meetingPoint,
                meetingTime: formData.meetingTime,
                difficulty: formData.difficulty,
                notes: formData.notes,
                submittedAt: new Date().toISOString()
            };

            if (trail) {
                baseEventPayload.trail = {
                    id: trail.properties?.id || trail.id,
                    name: trail.properties?.name || 'Percorso senza nome',
                    region: nameRegion,
                    length: trail.properties?.lengthKm || trail.properties?.length,
                    ascent: trail.properties?.ascentMeters || trail.properties?.ascent,
                    coordinates: trail.geometry?.coordinates
                };
            }

            if (poi) {
                baseEventPayload.poi = {
                    id: poi.properties?.id || poi.id,
                    name: poi.properties?.name || 'POI',
                    region: nameRegion,
                    type: poi.properties?.type || 'punto_interesse',
                    coordinates: poi.geometry?.coordinates
                };
            }

            const results = [];
            for (const eventDate of datesToCreate) {
                const response = await fetch(`${apiUrl}/api/events/propose`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`   // ← aggiunto token
                    },
                    body: JSON.stringify({ ...baseEventPayload, eventDate })
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.error || 'Errore durante l\'invio');
                }
                results.push(await response.json());
            }

            setSuccessMessage(formData.isRecurring
                ? `✅ ${results.length} eventi ricorrenti creati con successo!`
                : '✅ Evento attività creato con successo!'
            );
            setShowDonation(true);

        } catch (err) {
            setError(err.message === 'Failed to fetch'
                ? 'Impossibile connettersi al server. Verifica che il backend sia attivo.'
                : err.message || 'Errore durante l\'invio. Riprova più tardi.'
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const locationName = trail?.properties?.name || poi?.properties?.name || 'Location selezionata';

    return (
        <>
            <div className="event-proposal-overlay">
                <div className="event-proposal-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="event-proposal-header">
                        <h2>✨ Proponi Evento Attività</h2>
                        <button className="close-button" onClick={onClose}>✕</button>
                    </div>

                    <div className="trail-info">
                        <h3>📍 {locationName}</h3>
                        <div className="trail-details">
                            {trail && (
                                <>
                                    <span>📏 {trail.properties?.length || 'N/A'}</span>
                                    <span>⛰️ {trail.properties?.ascent || 'N/A'}</span>
                                    <span>⏱️ {trail.properties?.duration || 'N/A'}</span>
                                </>
                            )}
                            {poi && <span>🏠 {poi.properties?.type || 'POI'}</span>}
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="event-proposal-form">

                        {/* ══════════════════════════════════
                            ACCOUNT GOOGLE — prima sezione
                            (stesso pattern di TrailBuddyForm)
                        ══════════════════════════════════ */}
                        <div className="form-section">
                            <h3>👤 Il Tuo Account</h3>
                            {authLoading ? (
                                <div className="google-auth-loading"><span>⏳ Caricamento...</span></div>
                            ) : isAuthenticated && user ? (
                                <div className="google-user-info">
                                    <div className="google-user-card">
                                        {user.photoURL && (
                                            <img
                                                src={user.photoURL}
                                                alt="Avatar"
                                                className="google-avatar"
                                                referrerPolicy="no-referrer"
                                            />
                                        )}
                                        <div className="google-user-details">
                                            <span className="google-user-name">{user.displayName || 'Utente Google'}</span>
                                            <span className="google-user-email">{user.email}</span>
                                        </div>
                                        <button
                                            type="button"
                                            className="btn-logout"
                                            onClick={logout}
                                            title="Disconnetti"
                                        >✕</button>
                                    </div>
                                    <div className="form-group" style={{ marginTop: '12px' }}>
                                        <label htmlFor="organizerPhone">📱 Telefono / WhatsApp (opzionale)</label>
                                        <input
                                            type="tel"
                                            id="organizerPhone"
                                            name="organizerPhone"
                                            value={formData.organizerPhone}
                                            onChange={handleChange}
                                            placeholder="+39 123 456 7890"
                                        />
                                        <small style={{ color: '#888', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                                            Visibile ai partecipanti iscritti
                                        </small>
                                    </div>
                                    <p className="privacy-note">🔒 I tuoi contatti non saranno visibili pubblicamente</p>
                                </div>
                            ) : (
                                <div className="google-login-section">
                                    <p className="google-login-hint">
                                        Accedi con Google per proporre un evento.<br />
                                        <small>Nome e foto verranno compilati automaticamente.</small>
                                    </p>
                                    <button
                                        type="button"
                                        className="btn-google-login"
                                        onClick={handleGoogleLogin}
                                        disabled={isLoggingIn}
                                    >
                                        {isLoggingIn ? '⏳ Accesso in corso...' : (
                                            <>
                                                <svg className="google-icon" viewBox="0 0 24 24" width="20" height="20">
                                                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                                                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                                                </svg>
                                                Accedi con Google
                                            </>
                                        )}
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Dettagli evento */}
                        <div className="form-section">
                            <h3>📅 Dettagli Evento</h3>

                            <div className="form-group">
                                <label htmlFor="eventName">Nome Evento *</label>
                                <input
                                    type="text"
                                    id="eventName"
                                    name="eventName"
                                    value={formData.eventName}
                                    onChange={handleChange}
                                    placeholder="es. Escursione guidata al tramonto"
                                    required
                                />
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="eventDate">
                                        {formData.isRecurring ? 'Data Inizio *' : 'Data Evento *'}
                                    </label>
                                    <input
                                        type="date"
                                        id="eventDate"
                                        name="eventDate"
                                        value={formData.eventDate}
                                        onChange={handleChange}
                                        min={new Date().toISOString().split('T')[0]}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="meetingTime">Orario Ritrovo *</label>
                                    <input
                                        type="time"
                                        id="meetingTime"
                                        name="meetingTime"
                                        value={formData.meetingTime}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        name="isRecurring"
                                        checked={formData.isRecurring}
                                        onChange={handleChange}
                                    />
                                    <span>🔄 Evento ricorrente (settimanale)</span>
                                </label>
                            </div>

                            {formData.isRecurring && (
                                <div className="recurring-section">
                                    <div className="form-group">
                                        <label>Ripeti ogni: *</label>
                                        <div className="weekday-selector">
                                            {WEEKDAYS.map(day => (
                                                <button
                                                    key={day.id}
                                                    type="button"
                                                    className={`weekday-btn ${formData.recurringDays.includes(day.id) ? 'selected' : ''}`}
                                                    onClick={() => toggleWeekday(day.id)}
                                                    title={day.full}
                                                >
                                                    {day.short}
                                                </button>
                                            ))}
                                        </div>
                                        <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                                            Seleziona uno o più giorni della settimana
                                        </small>
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="recurringEndDate">Fino al: *</label>
                                        <input
                                            type="date"
                                            id="recurringEndDate"
                                            name="recurringEndDate"
                                            value={formData.recurringEndDate}
                                            onChange={handleChange}
                                            min={formData.eventDate || new Date().toISOString().split('T')[0]}
                                            required
                                        />
                                        <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                                            Ultimo giorno in cui può ripetersi l'evento
                                        </small>
                                    </div>
                                </div>
                            )}

                            <div className="form-group">
                                <label htmlFor="meetingPoint">Punto di Ritrovo *</label>
                                <input
                                    type="text"
                                    id="meetingPoint"
                                    name="meetingPoint"
                                    value={formData.meetingPoint}
                                    onChange={handleChange}
                                    placeholder="es. Parcheggio Rifugio XYZ"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="maxParticipants">Numero Max Partecipanti</label>
                                <input
                                    type="number"
                                    id="maxParticipants"
                                    name="maxParticipants"
                                    value={formData.maxParticipants}
                                    onChange={handleChange}
                                    min="1"
                                    placeholder="Lascia vuoto se illimitato"
                                />
                            </div>
                        </div>

                        {/* Descrizione */}
                        <div className="form-section">
                            <h3>📝 Descrizione e Note</h3>

                            <div className="form-group">
                                <label htmlFor="description">Descrizione Evento</label>
                                <textarea
                                    id="description"
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    rows="4"
                                    placeholder="Descrivi l'evento, cosa è incluso, cosa portare, ecc."
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="notes">Note Aggiuntive</label>
                                <textarea
                                    id="notes"
                                    name="notes"
                                    value={formData.notes}
                                    onChange={handleChange}
                                    rows="3"
                                    placeholder="Altre informazioni utili..."
                                />
                            </div>
                        </div>

                        {error && <div className="error-message">⚠️ {error}</div>}

                        <div className="form-actions">
                            <button type="button" onClick={onClose} className="btn-cancel">Annulla</button>
                            <button
                                type="submit"
                                disabled={isSubmitting || !isAuthenticated}
                                className={`btn-submit ${!isAuthenticated ? 'btn-disabled' : ''}`}
                                title={!isAuthenticated ? 'Accedi con Google per proporre' : ''}
                            >
                                {isSubmitting ? '⏳ Invio...'
                                    : !isAuthenticated ? '🔒 Accedi per Proporre'
                                        : formData.isRecurring ? '🚀 Crea Eventi Ricorrenti'
                                            : '🚀 Proponi Evento'}
                            </button>
                        </div>
                    </form>
                </div>

                {showDonation && (
                    <DonationModal
                        message={successMessage}
                        onClose={() => {
                            setShowDonation(false);
                            if (onSuccess) onSuccess([]);
                            onClose();
                        }}
                    />
                )}
            </div>
        </>
    );
}
